import React, { useEffect, useMemo, useState } from "react";
import { Ban, Trash2, X } from "lucide-react";
import {
  createBlockTime,
  deleteBlockTime,
  updateBlockTime,
  BlockTime,
} from "../services/blockTimes";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../auth/AuthContext";

interface StaffOption {
  id: number;
  name: string;
  color?: string;
}

function isEmployeeUser(user: any): boolean {
  const roles = user?.roles || [];
  const isAdmin = roles.some((r: any) => r?.name === "administrator");
  const isEmployee = roles.some((r: any) => r?.name === "employee");
  return isEmployee && !isAdmin;
}

interface BlockTimeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  staffList: StaffOption[];
  /** Prefilled when opening from a calendar slot */
  defaultStaffName?: string;
  defaultDate?: string;
  defaultStartTime?: string;
  /** When editing an existing block */
  editing?: BlockTime | null;
}

function to24h(time: string): string {
  const t = time.trim();
  if (/^\d{1,2}:\d{2}$/.test(t)) {
    const [h, m] = t.split(":").map(Number);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  const match = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return t;
  let h = Number(match[1]);
  const m = match[2];
  const ap = match[3].toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m}`;
}

function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const nh = Math.floor(wrapped / 60);
  const nm = wrapped % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

const BlockTimeSidebar: React.FC<BlockTimeSidebarProps> = ({
  isOpen,
  onClose,
  onSaved,
  staffList,
  defaultStaffName = "",
  defaultDate = "",
  defaultStartTime = "09:00",
  editing = null,
}) => {
  const toast = useToast();
  const { user } = useAuth();
  const employeeOnly = isEmployeeUser(user);
  const [employeeId, setEmployeeId] = useState<number>(0);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("09:30");
  const [title, setTitle] = useState("Blocked");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEdit = !!editing;

  const effectiveStaffList = useMemo(() => {
    if (!employeeOnly) return staffList;
    const selfId = Number(user?.id);
    const self = staffList.find((s) => s.id === selfId);
    if (self) return [self];
    if (selfId && user?.name) {
      return [{ id: selfId, name: user.name }];
    }
    return staffList;
  }, [employeeOnly, staffList, user]);

  useEffect(() => {
    if (!isOpen) return;

    if (editing) {
      setEmployeeId(
        employeeOnly ? Number(user?.id) || editing.employee_id : editing.employee_id
      );
      setDate(editing.date);
      setStartTime(editing.start_time);
      setEndTime(editing.end_time);
      setTitle(editing.title || "Blocked");
      setReason(editing.reason || "");
      return;
    }

    if (employeeOnly && user?.id) {
      setEmployeeId(Number(user.id));
    } else {
      const staff = effectiveStaffList.find((s) => s.name === defaultStaffName);
      setEmployeeId(staff?.id || effectiveStaffList[0]?.id || 0);
    }
    setDate(defaultDate || new Date().toLocaleDateString("en-CA"));
    const start = to24h(defaultStartTime || "09:00");
    setStartTime(start);
    setEndTime(addMinutes(start, 30));
    setTitle("Blocked");
    setReason("");
  }, [
    isOpen,
    editing,
    defaultStaffName,
    defaultDate,
    defaultStartTime,
    effectiveStaffList,
    employeeOnly,
    user,
  ]);

  const canSave = useMemo(() => {
    return !!employeeId && !!date && !!startTime && !!endTime && endTime > startTime;
  }, [employeeId, date, startTime, endTime]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!canSave) {
      toast.error("Please fill employee, date, and a valid time range");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee_id: employeeId,
        date,
        start_time: startTime,
        end_time: endTime,
        title: title.trim() || "Blocked",
        reason: reason.trim() || undefined,
      };
      if (isEdit && editing) {
        await updateBlockTime(editing.id, payload);
        toast.success("Blocked time updated");
      } else {
        await createBlockTime(payload);
        toast.success("Blocked time created");
      }
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to save blocked time"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this blocked time?")) return;
    setDeleting(true);
    try {
      await deleteBlockTime(editing.id);
      toast.success("Blocked time deleted");
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to delete blocked time"
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-gray-100 p-2 text-gray-700">
              <Ban size={18} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#402B69]">
                {isEdit ? "Edit blocked time" : "Block time"}
              </h3>
              <p className="text-xs text-gray-500">
                Blocks this staff from bookings in this range
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Staff
            </label>
            <select
              value={employeeId || ""}
              onChange={(e) => setEmployeeId(Number(e.target.value))}
              disabled={employeeOnly}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-600"
            >
              <option value="" disabled>
                Select staff
              </option>
              {effectiveStaffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {employeeOnly ? (
              <p className="mt-1 text-xs text-gray-500">
                You can only block your own time
              </p>
            ) : null}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Start
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                End
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Lunch, Meeting, Break"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Note (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Optional note"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 border-t bg-gray-50 px-5 py-4">
          {isEdit ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={16} />
              Delete
            </button>
          ) : null}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving || deleting}
            className="rounded-lg bg-[#885ABB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7748A4] disabled:opacity-50"
          >
            {saving ? "Saving..." : isEdit ? "Update" : "Block time"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockTimeSidebar;
